import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmModal from "../../common/ConfirmModal";
import { buildVaultQuizViewModel } from "./qmsVaultWorkspaceUtils";
import { fetchQuestionV2, fetchQuizV2, fetchQuizzesV2 } from "./qmsService";
import { getApiTable, getApiTableRow, pickApiRowValue } from "./qmsApiUtils";

function formatQuizOptionLabel(quiz) {
  const name = String(quiz?.QuizName || "Untitled quiz").trim();
  const mapped = Number(quiz?.MappedQuestionCount ?? 0);
  const bound = Number(quiz?.DocumentBindingCount ?? 0);
  const parts = [name];
  if (mapped > 0) parts.push(`${mapped} Q`);
  if (bound > 0) parts.push(`${bound} docs`);
  return parts.join(" · ");
}

function formatQuizDate(value) {
  if (value === "" || value == null) return "—";
  const text = String(value).trim();
  if (!text) return "—";

  const dotnetMatch = text.match(/\/Date\((-?\d+)\)\//);
  const parsed = dotnetMatch
    ? new Date(Number(dotnetMatch[1]))
    : new Date(text.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1"));

  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatAttemptLimit(value) {
  if (value === "" || value == null) return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num === 0 ? "Unlimited" : String(num);
}

function formatPassingScore(value) {
  if (value === "" || value == null) return "—";
  return `${value}%`;
}

function formatQuestionsToServe(mappedCount, serveCount) {
  const mapped = Number(mappedCount) || 0;
  const serve = Number(serveCount) || 0;
  if (serve > 0 && serve < mapped) return `${serve} per attempt (${mapped} in bank)`;
  return mapped > 0 ? String(mapped) : "0";
}

function formatDocumentUsage(count) {
  const num = Number(count) || 0;
  if (num === 0) return "Not used yet";
  return `${num} document${num === 1 ? "" : "s"}`;
}

function isCorrectOption(option) {
  return (
    Number(option?.IsCorrectAnswere) === 1 ||
    option?.IsCorrectAnswere === true ||
    Number(option?.IsCorrect) === 1 ||
    option?.IsCorrect === true
  );
}

function buildQuizSummaryFromRow(quiz) {
  if (!quiz) return null;
  const createdByName = String(
    pickApiRowValue(quiz, "CreatedByName", "createdByName", "CreatedBy", "createdBy") || "",
  ).trim();
  const createdAt = pickApiRowValue(
    quiz,
    "CreatedAt",
    "createdAt",
    "SystemDateTime",
    "systemDateTime",
  );

  return {
    quizId: String(pickApiRowValue(quiz, "QuizId", "quizId", "QuizzId", "quizzId") || ""),
    quizName: pickApiRowValue(quiz, "QuizName", "quizName") || "Quiz",
    passingPercentage: pickApiRowValue(quiz, "PassingPercentage", "passingPercentage"),
    attemptLimit: pickApiRowValue(quiz, "AttemptLimit", "attemptLimit"),
    mappedQuestionCount: Number(
      pickApiRowValue(quiz, "MappedQuestionCount", "mappedQuestionCount") ?? 0,
    ) || 0,
    questionsToServeCount: Number(
      pickApiRowValue(quiz, "QuestionsToServeCount", "questionsToServeCount") ?? 0,
    ) || 0,
    documentBindingCount: Number(
      pickApiRowValue(quiz, "DocumentBindingCount", "documentBindingCount") ?? 0,
    ) || 0,
    createdByName: createdByName || "—",
    createdAt,
  };
}

function mergeQuizSummary(base, detail) {
  if (!base) return detail;
  if (!detail) return base;

  const createdByName =
    detail.createdByName && detail.createdByName !== "—"
      ? detail.createdByName
      : base.createdByName;
  const createdAt = detail.createdAt ?? base.createdAt;
  const documentBindingCount = Math.max(
    Number(base.documentBindingCount) || 0,
    Number(detail.documentBindingCount) || 0,
  );

  return {
    ...base,
    ...detail,
    createdByName,
    createdAt,
    documentBindingCount,
  };
}

function VaultQuizSearchSelect({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  inputId = "vault-quiz-select",
  selectedLabel = "",
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedQuiz = useMemo(
    () => options.find((quiz) => String(quiz.QuizId) === String(value)) || null,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((quiz) =>
      formatQuizOptionLabel(quiz).toLowerCase().includes(needle),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      if (selectedQuiz) {
        setQuery(formatQuizOptionLabel(selectedQuiz));
      } else if (value && selectedLabel) {
        setQuery(selectedLabel);
      }
    }
    if (!value) {
      setQuery("");
    }
  }, [open, selectedQuiz, value, selectedLabel]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const handleSelect = (quiz) => {
    onChange(String(quiz.QuizId));
    setQuery(formatQuizOptionLabel(quiz));
    setOpen(false);
  };

  const handleInputChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    if (!next.trim()) {
      onChange("");
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`qms-vault-quiz-link__search-select${
        disabled ? " is-disabled" : ""
      }${open ? " is-open" : ""}`}
    >
      <div className="qms-vault-quiz-link__search-input-wrap">
        <i className="fas fa-search" aria-hidden="true" />
        <input
          id={inputId}
          type="text"
          className="form-control form-control-sm qms-vault-quiz-link__search-input"
          value={query}
          placeholder={loading ? "Loading quizzes..." : "Search and select a quiz"}
          disabled={disabled || loading}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={() => {
            if (!disabled && !loading) setOpen(true);
          }}
        />
        {value && !disabled ? (
          <button
            type="button"
            className="qms-vault-quiz-link__search-clear"
            aria-label="Clear quiz selection"
            onClick={() => {
              onChange("");
              setQuery("");
              setOpen(false);
            }}
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {open && !disabled && !loading ? (
        <div className="qms-vault-quiz-link__search-menu" role="listbox">
          {filteredOptions.length === 0 ? (
            <div className="qms-vault-quiz-link__search-empty">No quizzes match your search.</div>
          ) : (
            filteredOptions.map((quiz) => {
              const quizId = String(quiz.QuizId);
              const isSelected = quizId === String(value);
              return (
                <button
                  key={quizId}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`qms-vault-quiz-link__search-option${
                    isSelected ? " is-selected" : ""
                  }`}
                  onClick={() => handleSelect(quiz)}
                >
                  <span className="qms-vault-quiz-link__search-option-name">
                    {quiz.QuizName || "Untitled quiz"}
                  </span>
                  <span className="qms-vault-quiz-link__search-option-meta">
                    {Number(quiz.MappedQuestionCount ?? 0) || 0} questions
                    {Number(quiz.DocumentBindingCount ?? 0) > 0
                      ? ` · used on ${quiz.DocumentBindingCount} doc${
                          Number(quiz.DocumentBindingCount) === 1 ? "" : "s"
                        }`
                      : ""}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function QuizStat({ icon, label, value, tone = "default" }) {
  return (
    <div className={`qms-vault-quiz-link__stat is-${tone}`}>
      <span className="qms-vault-quiz-link__stat-icon" aria-hidden="true">
        <i className={icon} />
      </span>
      <div className="qms-vault-quiz-link__stat-body">
        <span className="qms-vault-quiz-link__stat-label">{label}</span>
        <strong className="qms-vault-quiz-link__stat-value">{value}</strong>
      </div>
    </div>
  );
}

function QuizQuestionPreview({ question }) {
  return (
    <article className="qms-vault-quiz-link__question">
      <div className="qms-vault-quiz-link__question-head">
        <span className="qms-vault-quiz-link__question-index">Q{question.index}</span>
        <p className="qms-vault-quiz-link__question-text">{question.Question}</p>
      </div>
      {question.options?.length ? (
        <ul className="qms-vault-quiz-link__options">
          {question.options.map((option, optionIndex) => {
            const correct = isCorrectOption(option);
            return (
              <li
                key={option.OptionId || `${question.QuestionId}-${optionIndex}`}
                className={`qms-vault-quiz-link__option${
                  correct ? " is-correct" : ""
                }`}
              >
                <span className="qms-vault-quiz-link__option-marker">
                  {option.SerialNumber || optionIndex + 1}
                </span>
                <span className="qms-vault-quiz-link__option-text">
                  {option.OptionText}
                </span>
                {correct ? (
                  <span className="qms-vault-quiz-link__option-badge" title="Correct answer">
                    <i className="fas fa-check" aria-hidden="true" />
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="qms-vault-quiz-link__options-empty">No answer options found.</p>
      )}
    </article>
  );
}

async function loadQuizQuestionPreview(userData, quizId) {
  const response = await fetchQuizV2(userData, quizId);
  const questionRows = getApiTable(response, "QuizQuestions");

  const questions = await Promise.all(
    questionRows.map(async (row, index) => {
      try {
        const questionResponse = await fetchQuestionV2(userData, row.QuestionId);
        const options = getApiTable(questionResponse, "QuestionOptions");
        return {
          QuestionId: row.QuestionId,
          Question: row.QuestionText || row.Question || "",
          index: index + 1,
          options: options.map((option) => ({
            OptionId: option.OptionId,
            SerialNumber: option.SerialNumber,
            OptionText: option.OptionText,
            IsCorrect: option.IsCorrect,
            IsCorrectAnswere: option.IsCorrect,
          })),
        };
      } catch {
        return {
          QuestionId: row.QuestionId,
          Question: row.QuestionText || row.Question || "",
          index: index + 1,
          options: [],
        };
      }
    }),
  );

  return {
    questions,
    totalQuestionCount: questionRows.length,
    previewTruncated: false,
  };
}

function QmsVaultQuizPanel({
  workspaceResponse,
  workflow,
  userData,
  saving = false,
  onLinkQuiz,
  onUnlinkQuiz,
}) {
  const [quizOptions, setQuizOptions] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [selectedQuizId, setSelectedQuizId] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState("");
  const [questionPreview, setQuestionPreview] = useState({
    questions: [],
    totalQuestionCount: 0,
    previewTruncated: false,
  });
  const [linkedQuizMeta, setLinkedQuizMeta] = useState(null);

  const linkedQuizView = useMemo(
    () => buildVaultQuizViewModel(workspaceResponse),
    [workspaceResponse],
  );

  const linkedQuiz = linkedQuizView.linkedQuiz;
  const isLinked = Boolean(linkedQuiz);
  const activeQuizId = isLinked ? linkedQuiz.quizId : selectedQuizId;

  const selectedQuizSummary = useMemo(() => {
    if (!activeQuizId) return null;

    const fromList = buildQuizSummaryFromRow(
      quizOptions.find((quiz) => String(quiz.QuizId) === String(activeQuizId)),
    );

    let base = fromList;
    if (!base && isLinked && linkedQuiz) {
      base = {
        quizId: linkedQuiz.quizId,
        quizName: linkedQuiz.quizName,
        passingPercentage: linkedQuizView.settings.passingPercentage,
        attemptLimit: linkedQuizView.settings.attemptLimit,
        mappedQuestionCount: linkedQuiz.mappedQuestionCount,
        questionsToServeCount: linkedQuiz.questionsToServeCount,
        documentBindingCount: linkedQuiz.documentBindingCount ?? 0,
        createdByName: linkedQuiz.createdByName || "—",
        createdAt: linkedQuiz.createdAt ?? null,
      };
    }
    if (!base) return null;

    if (linkedQuizMeta && String(linkedQuizMeta.quizId) === String(activeQuizId)) {
      return mergeQuizSummary(base, linkedQuizMeta);
    }

    return base;
  }, [activeQuizId, quizOptions, isLinked, linkedQuiz, linkedQuizMeta, linkedQuizView]);

  const pickerQuizOptions = useMemo(() => {
    if (!linkedQuiz?.quizId) return quizOptions;
    const linkedId = String(linkedQuiz.quizId);
    if (quizOptions.some((quiz) => String(quiz.QuizId) === linkedId)) {
      return quizOptions;
    }
    return [
      {
        QuizId: linkedQuiz.quizId,
        QuizName: linkedQuiz.quizName,
        MappedQuestionCount: linkedQuiz.mappedQuestionCount,
        DocumentBindingCount: linkedQuiz.documentBindingCount ?? 0,
      },
      ...quizOptions,
    ];
  }, [quizOptions, linkedQuiz]);

  const selectedQuizDisplayLabel = useMemo(() => {
    if (!selectedQuizSummary) return "";
    return formatQuizOptionLabel({
      QuizName: selectedQuizSummary.quizName,
      MappedQuestionCount: selectedQuizSummary.mappedQuestionCount,
      DocumentBindingCount: selectedQuizSummary.documentBindingCount,
    });
  }, [selectedQuizSummary]);

  useEffect(() => {
    if (!userData?.StaffId) return undefined;
    let cancelled = false;

    const loadQuizzes = async () => {
      setQuizLoading(true);
      try {
        const response = await fetchQuizzesV2(userData, {
          pageNumber: 1,
          pageSize: 200,
          searchText: "",
        });
        if (!cancelled) {
          setQuizOptions(getApiTable(response, "Quizzes"));
        }
      } catch {
        if (!cancelled) setQuizOptions([]);
      } finally {
        if (!cancelled) setQuizLoading(false);
      }
    };

    loadQuizzes();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    setSelectedQuizId(linkedQuiz?.quizId || "");
  }, [linkedQuiz?.quizId]);

  useEffect(() => {
    if (!userData?.StaffId || !activeQuizId) {
      setLinkedQuizMeta(null);
      return undefined;
    }

    let cancelled = false;
    fetchQuizV2(userData, activeQuizId)
      .then((response) => {
        const quiz = getApiTableRow(response, "Quiz", "Quizzes");
        if (!cancelled && quiz) {
          setLinkedQuizMeta(buildQuizSummaryFromRow(quiz));
        }
      })
      .catch(() => {
        if (!cancelled) setLinkedQuizMeta(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userData, activeQuizId]);

  useEffect(() => {
    if (!activeQuizId || !userData?.StaffId) {
      setQuestionPreview({ questions: [], totalQuestionCount: 0, previewTruncated: false });
      setQuestionsError("");
      setQuestionsLoading(false);
      return undefined;
    }

    if (isLinked && linkedQuizView.questions.length > 0) {
      setQuestionPreview({
        questions: linkedQuizView.questions,
        totalQuestionCount: linkedQuizView.questions.length,
        previewTruncated: false,
      });
      setQuestionsError("");
      setQuestionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setQuestionsLoading(true);
    setQuestionsError("");

    loadQuizQuestionPreview(userData, activeQuizId)
      .then((preview) => {
        if (!cancelled) setQuestionPreview(preview);
      })
      .catch((error) => {
        if (!cancelled) {
          setQuestionPreview({
            questions: [],
            totalQuestionCount: 0,
            previewTruncated: false,
          });
          setQuestionsError(error?.message || "Failed to load question preview.");
        }
      })
      .finally(() => {
        if (!cancelled) setQuestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeQuizId, isLinked, linkedQuizView.questions, userData]);

  const handleLink = async () => {
    if (!selectedQuizId || saving) return;
    await onLinkQuiz?.(selectedQuizId);
  };

  if (!workflow?.canManageQuiz && !linkedQuiz) {
    return (
      <div className="qms-doc-workspace__panel">
        <div className="qms-doc-workspace__panel-head">
          <div>
            <h6 className="qms-doc-workspace__panel-title">Quiz</h6>
            <p className="qms-doc-workspace__panel-desc">
              Quiz management is not available for this document stage.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const canShowQuizLinking = workflow?.canManageQuiz || isLinked;
  const previewMetaText = !activeQuizId
    ? "Select a quiz to preview questions"
    : questionsLoading
      ? "Loading questions..."
      : `${questionPreview.questions.length} shown${
          questionPreview.totalQuestionCount
            ? ` of ${questionPreview.totalQuestionCount}`
            : ""
        } · correct answers highlighted`;

  return (
    <div className="qms-doc-workspace__panel qms-vault-workspace__quiz-panel qms-vault-quiz-link">
      {canShowQuizLinking ? (
        <div className="qms-vault-quiz-link__split">
          <section className="qms-vault-quiz-link__settings-pane">
            <div className="qms-vault-quiz-link__pane-head">
              <h6>Quiz settings</h6>
              {selectedQuizSummary ? (
                <span
                  className={`qms-vault-quiz-link__status${
                    isLinked ? " is-linked" : " is-preview"
                  }`}
                >
                  {isLinked ? "Linked" : "Selected"}
                </span>
              ) : null}
            </div>

            <div className="qms-vault-quiz-link__settings-body">
              <div className="qms-vault-quiz-link__settings-intro">
                <strong>Quiz linking</strong>
                <p>
                  {workflow?.canManageQuiz
                    ? "Search a quiz from Quiz Manager, review settings and questions, then link."
                    : "Review the linked quiz settings and question preview below."}
                </p>
              </div>

              {!workflow?.canManageQuiz && selectedQuizSummary ? (
                <div className="qms-vault-quiz-link__linked-quiz-readonly">
                  <label className="qms-vault-workspace__field-label">Linked quiz</label>
                  <div
                    className="qms-vault-quiz-link__linked-quiz-value"
                    title={selectedQuizSummary.quizName}
                  >
                    {selectedQuizSummary.quizName}
                  </div>
                </div>
              ) : null}

              {workflow?.canManageQuiz ? (
                <div className="qms-vault-quiz-link__picker-inline">
                  <label className="qms-vault-workspace__field-label" htmlFor="vault-quiz-select">
                    Select quiz
                  </label>
                  <VaultQuizSearchSelect
                    inputId="vault-quiz-select"
                    options={pickerQuizOptions}
                    value={selectedQuizId}
                    selectedLabel={selectedQuizDisplayLabel}
                    loading={quizLoading}
                    disabled={saving || isLinked}
                    onChange={setSelectedQuizId}
                  />
                  <div className="qms-vault-workspace__action-row qms-vault-quiz-link__picker-actions">
                    {!isLinked ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={saving || !selectedQuizId}
                        onClick={handleLink}
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                            Linking…
                          </>
                        ) : (
                          "Link quiz"
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={saving}
                        onClick={() => setConfirmUnlink(true)}
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                            Unlinking…
                          </>
                        ) : (
                          "Unlink"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ) : null}

              {selectedQuizSummary ? (
                <div className="qms-vault-quiz-link__detail-card">
                  <div className="qms-vault-quiz-link__stats">
                    <QuizStat
                      icon="fas fa-percent"
                      label="Passing score"
                      value={formatPassingScore(selectedQuizSummary.passingPercentage)}
                      tone="teal"
                    />
                    <QuizStat
                      icon="fas fa-redo"
                      label="Attempt limit"
                      value={formatAttemptLimit(selectedQuizSummary.attemptLimit)}
                      tone="blue"
                    />
                    <QuizStat
                      icon="fas fa-list-ol"
                      label="Questions"
                      value={formatQuestionsToServe(
                        selectedQuizSummary.mappedQuestionCount,
                        selectedQuizSummary.questionsToServeCount,
                      )}
                      tone="violet"
                    />
                    <QuizStat
                      icon="fas fa-file-alt"
                      label="Used on documents"
                      value={formatDocumentUsage(selectedQuizSummary.documentBindingCount)}
                      tone="amber"
                    />
                    <QuizStat
                      icon="fas fa-user"
                      label="Created by"
                      value={selectedQuizSummary.createdByName}
                      tone="slate"
                    />
                    <QuizStat
                      icon="fas fa-calendar-day"
                      label="Created on"
                      value={formatQuizDate(selectedQuizSummary.createdAt)}
                      tone="slate"
                    />
                  </div>
                </div>
              ) : (
                <div className="qms-vault-quiz-link__settings-empty">
                  Search and select a quiz to view its settings here.
                </div>
              )}
            </div>
          </section>

          <section className="qms-vault-quiz-link__preview-pane">
            <div className="qms-vault-quiz-link__preview-head">
              <div>
                <h6>Question preview</h6>
                {selectedQuizSummary?.quizName ? (
                  <p className="qms-vault-quiz-link__preview-quiz-name">
                    {selectedQuizSummary.quizName}
                  </p>
                ) : null}
              </div>
              <span className="text-muted small">{previewMetaText}</span>
            </div>

            <div className="qms-vault-quiz-link__preview-body">
              {!activeQuizId ? (
                <div className="qms-vault-quiz-link__preview-empty">
                  Choose a quiz in the settings panel to preview its questions.
                </div>
              ) : null}

              {activeQuizId && questionsLoading ? (
                <div className="qms-vault-quiz-link__loading">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading questions...</span>
                  </div>
                  <span>Loading question preview...</span>
                </div>
              ) : null}

              {activeQuizId && questionsError ? (
                <div className="alert alert-danger py-2 px-3 mb-0" role="alert">
                  {questionsError}
                </div>
              ) : null}

              {activeQuizId &&
              !questionsLoading &&
              !questionsError &&
              questionPreview.questions.length === 0 ? (
                <div className="qms-vault-quiz-link__preview-empty">
                  This quiz has no mapped questions yet. Add questions in Quiz Manager.
                </div>
              ) : null}

              {activeQuizId && !questionsLoading && questionPreview.questions.length > 0 ? (
                <>
                  <div className="qms-vault-quiz-link__questions">
                    {questionPreview.questions.map((question) => (
                      <QuizQuestionPreview
                        key={question.QuestionId}
                        question={question}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={confirmUnlink}
        title="Unlink quiz"
        message="Remove the linked quiz from this document? Staff will no longer need to pass this quiz for this document."
        confirmText="Unlink"
        confirmButtonClass="btn-danger"
        icon="fas fa-unlink text-danger"
        onClose={() => setConfirmUnlink(false)}
        onConfirm={async () => {
          setConfirmUnlink(false);
          await onUnlinkQuiz?.();
        }}
      />
    </div>
  );
}

export default QmsVaultQuizPanel;
